import { Fragment, useState } from "react";
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  createColumnHelper,
  SortingState,
} from "@tanstack/react-table";

import { ISiteSummary } from "../../models/Site";
import { ButtonInput } from "../shared/Inputs/ButtonInput";

interface SitesTableProps {
  sites: ISiteSummary[];
  setSite: (site: ISiteSummary) => void;
  setAssignSite: (site: ISiteSummary) => void;
}

export default function SitesTable({ sites, setSite, setAssignSite }: SitesTableProps) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const colHelper = createColumnHelper<ISiteSummary>();

  const cols = [
    colHelper.accessor("name", {
      header: "Name",
      sortingFn: "auto",
      cell: (info) => (
        <span className="flex items-center gap-2">
          <span className="ml-4">{info.getValue()}</span>
          {info.row.original.isDefault && (
            <span className="inline-flex items-center rounded-full bg-primary-50 px-2 py-0.5 text-xs font-medium text-primary-700 ring-1 ring-inset ring-primary-600/20">
              Default
            </span>
          )}
        </span>
      )
    }),
    colHelper.accessor("shortCode", {
      header: "Short Code",
      sortingFn: "auto",
      cell: (info) => info.getValue() || "-"
    }),
    colHelper.accessor("yardLocationCount", {
      header: "Yard Locations",
      sortingFn: "auto",
    }),
    colHelper.accessor("binCount", {
      header: "Warehouse Locations",
      sortingFn: "auto",
    }),
    colHelper.accessor("vehicleCount", {
      header: "Vehicles",
      sortingFn: "auto",
    }),
    colHelper.accessor("partCount", {
      header: "Parts In Stock",
      sortingFn: "auto",
    }),
    colHelper.display({
      id: "actions",
      header: "",
      cell: (info) => (
        <ButtonInput
          label="Assign Locations"
          isSubmit={false}
          classes={undefined}
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            setAssignSite(info.row.original);
          }}
        />
      )
    }),
  ];

  const table = useReactTable({
    data: sites,
    columns: cols,
    state: {
      sorting: sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <div className="mt-4 bg-white overflow-auto">
      <table className="min-w-full overflow-scroll divide-y border divide-gray-300">
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id} className="bg-primary-50 text-gray-800">
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  scope="col"
                  className="py-3.5 px-3 text-sm font-semibold lg:table-cell text-left"
                >
                  {header.isPlaceholder
                    ? null
                    : (
                      <div
                        {...{
                          className: header.column.getCanSort()
                            ? 'cursor-pointer select-none'
                            : '',
                          onClick: header.column.getToggleSortingHandler(),
                        }}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {{
                          asc: ' ▲',
                          desc: ' ▼',
                        }[header.column.getIsSorted() as string] ?? null}
                      </div>
                    )}
                </th>
              ))}
            </tr>
          ))}
        </thead>

        <tbody>
          {table.getRowModel().rows.length === 0 && (
            <tr>
              <td colSpan={cols.length} className="px-3 py-6 text-sm text-center text-gray-500">
                No sites yet. Add one to start grouping yard and warehouse locations by depot.
              </td>
            </tr>
          )}
          {table.getRowModel().rows.map((row, i) => (
            <Fragment key={i}>
              <tr
                key={row.id}
                onClick={() => setSite(row.original)}
                className={"hover:bg-gray-100 hover:cursor-pointer"}
              >
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className="px-3 py-3 border text-sm">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}
